import type { Notification } from '../api/types.js';

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'just now';
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getIcon(type: string): string {
  switch (type) {
    case 'session.status_changed':
      return '\u25B6';
    case 'session.permission_pending':
      return '\u26A0';
    case 'session.ended':
      return '\u25A0';
    case 'server.status_changed':
      return '\u2699';
    case 'cost.threshold_exceeded':
      return '$';
    case 'item.ready':
      return '\u25C6';
    case 'inbox.digest':
      return '\u25C7';
    default:
      return '\u2022';
  }
}

function getDescription(notification: Notification): string {
  const { event } = notification;
  switch (event.type) {
    case 'session.status_changed':
      return `Session ${event.data.status} on ${event.serverId}`;
    case 'session.permission_pending':
      return `Permission required on ${event.serverId}`;
    case 'session.ended':
      return `Session ended on ${event.serverId}`;
    case 'server.status_changed':
      return `Server ${event.serverId} is ${event.data.status}`;
    case 'cost.threshold_exceeded':
      return `Cost threshold exceeded on ${event.serverId}`;
    case 'item.ready':
      return typeof event.data.title === 'string' ? event.data.title : `Decision ready on ${event.serverId}`;
    case 'inbox.digest':
      return typeof event.data.title === 'string' ? event.data.title : `Decisions ready on ${event.serverId}`;
    default:
      return `Event on ${event.serverId}`;
  }
}

function getLink(notification: Notification): string {
  const { event } = notification;
  if ((event.type === 'item.ready' || event.type === 'inbox.digest') && typeof event.data.url === 'string' && event.data.url.startsWith('/')) return event.data.url;
  if (event.sessionId) {
    return `/servers/${event.serverId}/sessions/${event.sessionId}`;
  }
  return `/servers/${event.serverId}`;
}

function getIconColor(type: string): string {
  switch (type) {
    case 'session.permission_pending':
      return 'var(--color-warn)';
    case 'session.ended':
      return 'var(--color-text-3)';
    case 'cost.threshold_exceeded':
      return 'var(--color-error)';
    case 'server.status_changed':
      return 'var(--color-brand)';
    case 'item.ready':
    case 'inbox.digest':
      return 'var(--color-brand-light)';
    default:
      return 'var(--color-text-3)';
  }
}

interface NotificationItemProps {
  notification: Notification;
  onClick: (id: string, link: string) => void;
}

/** The left edge says how much it matters, as in « Waiting on you » */
function edgeOf(notification: Notification): string {
  const { event } = notification;
  if (event.type === 'item.ready') return event.data.priority === 'urgent' ? 'var(--color-error)' : 'var(--color-brand)';
  if (event.type === 'inbox.digest') return 'var(--color-brand)';
  if (event.type === 'session.permission_pending') return 'var(--color-warn)';
  if (event.type === 'cost.threshold_exceeded') return 'var(--color-error)';
  return 'var(--color-border-2)';
}

export function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const link = getLink(notification);
  const unread = !notification.read;

  return (
    <button
      type="button"
      onClick={() => onClick(notification.id, link)}
      className="flex w-full items-baseline gap-3 py-2.5 pl-3 pr-4 text-left hover:bg-[var(--color-surface-2)] focus-visible:bg-[var(--color-surface-2)] focus-visible:outline-none"
      style={{ borderLeft: `3px solid ${unread ? edgeOf(notification) : 'transparent'}` }}
    >
      <span className="w-3 flex-shrink-0 text-xs" style={{ color: getIconColor(notification.event.type) }} aria-hidden="true">
        {getIcon(notification.event.type)}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm ${unread ? 'font-medium' : ''}`} style={{ color: unread ? 'var(--color-text)' : 'var(--color-text-3)' }}>
          {getDescription(notification)}
        </span>
        <span className="mt-0.5 block text-xs" style={{ color: 'var(--color-text-3)' }}>
          {notification.event.serverId}, {formatTimeAgo(notification.createdAt)}
          {unread && <span className="sr-only">, new</span>}
        </span>
      </span>
    </button>
  );
}
